import { useState, useEffect, useCallback } from 'react';
import { followUpsAPI, leadsAPI, teamAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { Clock, AlertTriangle, CheckCircle2, Phone, Mail, Calendar, Building2, Plus, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const TYPE_ICONS = { call: Phone, email: Mail, meeting: Calendar, visit: Building2 };

export default function FollowUpsPage() {
  const [followUps, setFollowUps] = useState([]);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();

  const fetchFollowUps = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (tab !== 'all') params.status = tab;
      const res = await followUpsAPI.list(params);
      setFollowUps(res.data.follow_ups);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchFollowUps(); }, [fetchFollowUps]);

  const markDone = async (id) => {
    await followUpsAPI.update(id, { status: 'completed' });
    toast.success('Marked as completed');
    fetchFollowUps();
  };

  const overdue = followUps.filter(f => new Date(f.due_date) < new Date() && f.status === 'pending');
  const upcoming = followUps.filter(f => new Date(f.due_date) >= new Date() && f.status === 'pending');
  const completed = followUps.filter(f => f.status === 'completed');

  return (
    <div className="space-y-5 fade-in" data-testid="followups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Opvolgingen</h1>
          <p className="text-muted-foreground text-sm">{total} total</p>
        </div>
        <div className="flex items-center gap-2">
          {overdue.length > 0 && (
            <Badge variant="destructive" className="gap-1.5 px-3 py-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {overdue.length} verlopen
            </Badge>
          )}
          <Button size="sm" className="gap-1.5" onClick={() => setShowAdd(true)} data-testid="add-followup-btn">
            <Plus className="w-4 h-4" /> Nieuwe opvolging
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Openstaand</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Afgerond</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 mt-4">
          {overdue.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-destructive flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Verlopen ({overdue.length})</h3>
              {overdue.map((fu) => <FollowUpCard key={fu.id} fu={fu} isOverdue navigate={navigate} onMarkDone={markDone} />)}
            </div>
          )}
          {upcoming.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" />Aankomend ({upcoming.length})</h3>
              {upcoming.map((fu) => <FollowUpCard key={fu.id} fu={fu} navigate={navigate} onMarkDone={markDone} />)}
            </div>
          )}
          {overdue.length === 0 && upcoming.length === 0 && !loading && (
            <div className="text-center py-16 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Alles bijgewerkt! Geen openstaande opvolgingen.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-2 mt-4">
          {completed.map((fu) => <FollowUpCard key={fu.id} fu={fu} completed navigate={navigate} />)}
          {completed.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground text-center py-8">Geen afgeronde opvolgingen</p>
          )}
        </TabsContent>
      </Tabs>

      <AddFollowUpDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={fetchFollowUps}
      />
    </div>
  );
}

function AddFollowUpDialog({ open, onClose, onSuccess }) {
  const { settings } = useSettings();
  const followUpTypes = (settings.follow_up_types || []).map(t => typeof t === 'object' ? t : { key: t, label: t });
  const defaultType = followUpTypes[0]?.key || 'call';

  const [leadId, setLeadId] = useState('');
  const [type, setType] = useState(defaultType);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLeadId('');
    setType(defaultType);
    setDueDate('');
    setNotes('');
    setAssignedTo('');
    setSearch('');
    setLoadingLeads(true);
    leadsAPI.list({ limit: 500 })
      .then(res => setLeads(res.data.leads || []))
      .catch(() => setLeads([]))
      .finally(() => setLoadingLeads(false));
    teamAPI.members().then(res => setTeamMembers(res.data || [])).catch(() => setTeamMembers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filteredLeads = leads.filter(l => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (l.name || '').toLowerCase().includes(q) ||
           (l.email || '').toLowerCase().includes(q) ||
           (l.phone || '').toLowerCase().includes(q);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!leadId) { toast.error('Selecteer een lead'); return; }
    if (!dueDate) { toast.error('Selecteer een datum'); return; }
    setSaving(true);
    try {
      await followUpsAPI.create({
        lead_id: leadId,
        type,
        due_date: new Date(dueDate).toISOString(),
        notes,
        assigned_to: assignedTo || null,
      });
      toast.success('Opvolging aangemaakt');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Aanmaken mislukt';
      toast.error(typeof msg === 'string' ? msg : 'Aanmaken mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="add-followup-dialog">
        <DialogHeader><DialogTitle>Nieuwe opvolging</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Lead</Label>
            <Input
              type="text"
              placeholder="Zoek lead op naam, e-mail of telefoon..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-2"
              data-testid="followup-lead-search"
            />
            <select
              value={leadId || ""}
              onChange={(e) => setLeadId(e.target.value)}
              required
              data-testid="followup-lead-select"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">{loadingLeads ? 'Leads laden...' : '-- Selecteer een lead --'}</option>
              {filteredLeads.map(l => (
                <option key={l.id} value={l.id}>
                  {l.name}{l.email ? ` (${l.email})` : ''}
                </option>
              ))}
            </select>
            {!loadingLeads && filteredLeads.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">Geen leads gevonden</p>
            )}
          </div>

          <div>
            <Label>Type</Label>
            <select
              value={type || ""}
              onChange={(e) => setType(e.target.value)}
              data-testid="followup-type-select"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {followUpTypes.length === 0 && <option value="call">call</option>}
              {followUpTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <Label>Datum & tijd</Label>
            <Input
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              data-testid="followup-date-input"
            />
          </div>

          <div>
            <Label>Notities</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optionele notities..."
              data-testid="followup-notes-input"
            />
          </div>

          {teamMembers.length > 0 && (
            <div>
              <Label className="flex items-center gap-1"><User className="w-3 h-3" /> Toewijzen aan</Label>
              <select
                value={assignedTo || ''}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
              >
                <option value="">Automatisch (ik)</option>
                {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Annuleren</Button>
            <Button type="submit" disabled={saving || !leadId || !dueDate} data-testid="save-followup-btn">
              {saving ? 'Opslaan...' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpCard({ fu, isOverdue, completed, navigate, onMarkDone }) {
  const Icon = TYPE_ICONS[fu.type] || Clock;
  const { formatDate } = useSettings();
  const dueDate = new Date(fu.due_date);
  const isToday = dueDate.toDateString() === new Date().toDateString();

  return (
    <Card className={`border-border/60 ${isOverdue ? 'border-red-300 dark:border-red-900/60' : ''} hover:bg-muted/20 transition-colors`} data-testid={`followup-${fu.id}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          completed ? 'bg-green-100 dark:bg-green-900/30' :
          isOverdue ? 'bg-red-100 dark:bg-red-900/30' :
          'bg-muted'
        }`}>
          {completed ? <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" /> :
           isOverdue ? <AlertTriangle className="w-4 h-4 text-red-500" /> :
           <Icon className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/leads/${fu.lead_id}`)}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium capitalize">{fu.type}</span>
            <span className="text-xs text-muted-foreground">with</span>
            <span className="text-sm font-medium">{fu.lead_name}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs ${isOverdue ? 'text-red-500 font-medium' : isToday ? 'text-amber-500 font-medium' : 'text-muted-foreground'}`}>
              {isToday ? 'Today' : formatDate(fu.due_date)} {dueDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            {fu.notes && <span className="text-xs text-muted-foreground truncate">- {fu.notes}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {fu.assigned_to_name && (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-semibold" title={fu.assigned_to_name}>
              {fu.assigned_to_name.charAt(0).toUpperCase()}
            </span>
          )}
          {!completed && onMarkDone && (
            <Button size="sm" variant={isOverdue ? "destructive" : "default"} className="h-8 text-xs gap-1.5 shadow-sm" onClick={() => onMarkDone(fu.id)} data-testid={`complete-fu-${fu.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark Done
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
